/** Los códigos, como dato. Existe para que un esquema pueda VALIDAR contra
 *  ellos: una condición se guarda con uno de estos códigos, y sin lista runtime
 *  un typo crearía una condición fantasma que ninguna pantalla sabría nombrar.
 *  El `satisfies` obliga a que la lista y el tipo no se separen. */
export const CAP_DOSSIER_FINDING_CODES = [
    'subject_area_short',
    'no_outcome_evaluated',
    'outcomes_partially_evaluated',
    'non_academic_unattested',
    'course_without_ccd',
    'courses_outside_cap_areas',
    'no_compliance_evidence',
];
const SEVERITY_ORDER = {
    blocking: 0,
    concern: 1,
    note: 2,
};
/**
 * La portada del expediente, derivada del paquete que el centro presentó.
 *
 * Pura: no lee repositorios ni reloj. Lo que no está en el paquete no aparece en
 * la portada — si una ronda cruzó sin auditoría, lo dice en vez de mostrar ceros
 * que parecerían medidos.
 */
export function buildCapDossierCover(packet) {
    const findings = [];
    const digest = packet.compliance;
    if (!digest) {
        findings.push({ code: 'no_compliance_evidence', severity: 'blocking' });
    }
    else {
        // Cada área lleva su propio mínimo: un excedente en una NO compensa a otra.
        // Por eso se listan una por una en vez de un total que se vería holgado.
        for (const area of digest.subjectAreas) {
            if (!area.met) {
                findings.push({
                    code: 'subject_area_short',
                    severity: 'blocking',
                    area: area.code,
                    // `gapTcu` ya lo calcula la auditoría: recalcularlo acá sería una
                    // segunda definición de lo mismo, libre de divergir.
                    params: { missing: area.gapTcu, min: area.minTcu },
                });
            }
        }
        const unevaluatedPlos = digest.plos.filter((p) => !p.evaluated);
        if (unevaluatedPlos.length > 0) {
            const todos = unevaluatedPlos.length === digest.plos.length;
            findings.push({
                // Ninguno evaluado no es «más de lo mismo»: significa que el programa no
                // muestra CÓMO comprueba lo que enseña. Merece decirse distinto.
                code: todos ? 'no_outcome_evaluated' : 'outcomes_partially_evaluated',
                severity: todos ? 'blocking' : 'concern',
                params: { pending: unevaluatedPlos.length, total: digest.plos.length },
                outcomeIds: unevaluatedPlos.map((p) => p.sourceOutcomeId),
            });
        }
        const unattestedKeys = digest.nonAcademic.filter((n) => !n.met).map((n) => n.key);
        if (unattestedKeys.length > 0) {
            findings.push({
                // El revisor no puede verificarlos por su cuenta: si el centro no los
                // firma, no hay nadie que responda por ellos.
                code: 'non_academic_unattested',
                severity: 'blocking',
                params: { pending: unattestedKeys.length, total: digest.nonAcademic.length },
                nonAcademicKeys: unattestedKeys,
            });
        }
        if (digest.coursesWithoutCcd.length > 0) {
            findings.push({
                code: 'course_without_ccd',
                severity: 'blocking',
                params: { count: digest.coursesWithoutCcd.length },
            });
        }
        if (digest.coursesOutsideCapAreas.length > 0) {
            findings.push({
                // No incumple nada: son horas reales que este CAP no pide. Se dice para
                // que el revisor entienda por qué el total supera el mínimo mientras dos
                // áreas quedan cortas.
                code: 'courses_outside_cap_areas',
                severity: 'note',
                params: {
                    count: digest.coursesOutsideCapAreas.length,
                    tcu: digest.coursesOutsideCapAreas.reduce((sum, c) => sum + c.tcu, 0),
                },
            });
        }
    }
    findings.sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]);
    const counts = { blocking: 0, concern: 0, note: 0 };
    for (const f of findings)
        counts[f.severity] += 1;
    return {
        overallStatus: digest?.overallStatus ?? 'unknown',
        totalMinTcu: digest?.totalMinTcu ?? 0,
        totalActualTcu: digest?.totalActualTcu ?? 0,
        stacked: digest?.stackedPriorCapRecognized ?? false,
        findings,
        counts,
        evidence: {
            courses: packet.ccdAnnex?.length ?? 0,
            sections: packet.sections.length,
            attestations: packet.attestations.length,
            hasMatrix: !!packet.matrixAnnex,
        },
    };
}
//# sourceMappingURL=cap-dossier.service.js.map